<script setup lang="ts">
import type { SearchFormSelectProps } from '@autobid/ui/types/components/Select'
import { useSearchStore } from '@autobid/nuxt-pinia-store/store/searchStore/useSearchStore'
import ChipsButton from '@autobid/ui/components/common/Chips/ChipsButton.vue'
import { useApps } from '@autobid/ui/composables/useApps'
import { useDictionary } from '@autobid/ui/composables/useDictionary'
import { CUSTOM_CHECKBOX_ID } from '@autobid/ui/constants/CUSTOM_CHECKBOX_ID'
import { VEHICLE_TYPE_KEY } from '@autobid/ui/constants/VEHICLE_TYPE_KEY'
import { convertKwToHp } from '@autobid/ui/utils/convertKwToHp'
import { storeToRefs } from 'pinia'

const props = defineProps<Props>()
const { data: apps } = useApps()
const { dictionary: entireDictionary } = useDictionary({ enabled: process.client })
const { t } = useI18n()

const { allDisplayingForms: displayingForms } = storeToRefs(useSearchStore())

type FormSchema = Partial<SearchFormSelectProps['context']> & {
  $cmp?: string
  $formkit?: string
  props?: {
    name?: string
    label?: string
  }
}

const customData = computed(() => {
  const formSchemas = displayingForms.value
    .map(item => item.formSchema)
    .flat()

  return formSchemas as unknown as FormSchema[]
})

interface Props {
  chipKey: string
  chipId?: string | number | boolean
}

const displayItem = computed(() => {
  const [key, dictionary] = props.chipKey.split('-')

  const APP_ID = 'appId'

  const isCustomCheckbox = customData.value.find((item) => {
    return item.props?.name === key && item.$cmp === CUSTOM_CHECKBOX_ID
  })

  if (isCustomCheckbox) {
    return isCustomCheckbox.props?.label
  }

  if (APP_ID === key) {
    return apps.value?.items.find(
      app => String(app.id) === String(props.chipId),
    )?.name
  }

  const itemInDictionary
    = entireDictionary.value?.[key] ?? entireDictionary.value?.[dictionary]

  const customGroup = customData.value.find((item) => {
    const match
      = item.name === key
        || (dictionary?.length && item.name === dictionary)
        || item.name === props.chipKey

    const isVehicleType
      = key === VEHICLE_TYPE_KEY && item.$cmp === 'VehicleType'

    return match || isVehicleType
  })

  const WITHOUT_LABEL = ['e65', 'e345']

  const getParentLabel = () => {
    const label
      = customGroup?.label
        ?? customGroup?.props?.label
        ?? customGroup?.placeholder
        ?? itemInDictionary?.label

    if (!label || WITHOUT_LABEL.includes(props.chipKey)) {
      return ''
    }
    return `${label} -`
  }

  const parentLabel = getParentLabel()
  const formatChipValue = (value: string | number | boolean | undefined) => {
    if (value === undefined || value === true || value === 'true') {
      return parentLabel.replace(' -', '')
    }

    return `${parentLabel} ${value}`.trim()
  }

  if (itemInDictionary?.options?.length) {
    const label = itemInDictionary.options.find((item) => {
      return (
        item.id.toString() === dictionary
        || item.id.toString() === key
        || item.id.toString() === props.chipId?.toString()
      )
    })?.label

    if (label) {
      return formatChipValue(label)
    }

    return formatChipValue(props.chipId)
  }

  if (!customGroup && itemInDictionary) {
    return itemInDictionary.label
  }

  if (!customGroup?.options) {
    return formatChipValue(props.chipId)
  }

  const customItem = customGroup.options.find((option) => {
    if (typeof option !== 'object') {
      return [key, props.chipId].some(
        value => value !== undefined && String(option) === String(value),
      )
    }
    return [key, props.chipId].some(
      value => value !== undefined && String(option.id) === String(value),
    )
  })

  let customItemValue = (function () {
    if (!customItem && customGroup.allowCustomValue) {
      return props.chipId
    }

    if (typeof customItem === 'object' && 'name' in customItem) {
      return customItem.name
    }
    if (typeof customItem === 'object' && 'label' in customItem) {
      return customItem.label
    }

    return customItem
  }())

  if (customGroup.$formkit === 'PowerSelect') {
    customItemValue = t('search-form.power-select.option-label', { kw: customItemValue, hp: convertKwToHp(Number(customItemValue)) })
  }

  return formatChipValue(customItemValue ?? props.chipId)
})
</script>

<template>
  <ChipsButton>
    <template v-if="displayItem">
      {{ displayItem }}
    </template>
    <slot v-else />
  </ChipsButton>
</template>
